import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { APIError, getContextId, ListResponse, ResponseWrapper, useSearchParam } from '@tlx/astro-shared';
import { useTskContext } from '@Hooks/useTskContext';
import { useIntl } from 'react-intl';
import { callApi, isResponseWrapper, MethodType } from '@Util/api.ts';
import { SalaryV2TransactionDTO } from '@Models/model.types.ts';
import { createSalaryURL } from '@Page/SalaryRun/hooks/createSalaryAPI.ts';
import { CreateSalaryModalStandard } from './CreateSalaryModalStandard';
import { CreateSalaryModalInformSeveral } from './CreateSalaryModalInformSeveral';

export type CreateSalaryDTO = {
    year: number;
    month: number;
    date: string;
    description: string;
    simplifiedPayment: boolean;
    regularSalary: boolean;
    travelReports: boolean;
    openPosts: boolean;
    employeeExpenses: boolean;
    isHistorical: boolean;
};

export function CreateSalaryModal({
    handleClose,
    defaultPeriod,
    employmentIds,
}: {
    handleClose: Dispatch<SetStateAction<boolean>>;
    defaultPeriod: Date;
    employmentIds: number[];
}) {
    const intl = useIntl();
    const [period] = useState(defaultPeriod);
    const [awaitingResponse, setAwaitingResponse] = useState(false);
    const isHistorical = !!useSearchParam('isHistorical');
    const [error, setError] = useState('');

    const [firstWageTransactionId, setFirstWageTransactionId] = useState(0);
    const [numberTransactions, setNumberTransactions] = useState(1);
    const [numberEmployees, setNumberEmployees] = useState(0);

    const { maxEmployeesPerSalaryTransaction } = useTskContext();
    const severalTransactionsCreated = numberTransactions > 1;

    function closeDialogAndRedirect() {
        setAwaitingResponse(false);
        handleClose(false);
        window.location.assign(
            `/execute/salary?salaryTransactionId=${firstWageTransactionId}&contextId=${getContextId()}`,
        );
    }

    useEffect(() => {
        if (firstWageTransactionId > 0 && !severalTransactionsCreated) {
            closeDialogAndRedirect();
        }
    }, [firstWageTransactionId, severalTransactionsCreated]);

    const submitForm = async (event: React.FormEvent<HTMLFormElement>) => {
        setError('');
        setAwaitingResponse(true);
        event.stopPropagation();
        event.preventDefault();
        const formDataObject = new FormData(event.currentTarget);
        const formData = Object.fromEntries(formDataObject.entries());
        const createSalaryDTO: CreateSalaryDTO = {
            year: period.getFullYear(),
            month: period.getMonth() + 1,
            date: String(formData.date),
            description: String(formData.description),
            simplifiedPayment: formData?.simplifiedPayment === 'on',
            regularSalary: formData?.regularSalary === 'on',
            travelReports: formData?.travelReports === 'on',
            openPosts: formData?.openPosts === 'on',
            employeeExpenses: formData?.employeeExpenses === 'on',
            isHistorical: isHistorical,
        };
        const { response, error } = await callApi<
            ResponseWrapper<SalaryV2TransactionDTO> | ListResponse<SalaryV2TransactionDTO>
        >(createSalaryURL(createSalaryDTO), MethodType.POST, JSON.stringify(employmentIds));
        if (error) {
            setAwaitingResponse(false);
            let errorMessage = intl.formatMessage({
                id: 'validation_unexpected_error',
            });
            if (error instanceof APIError) {
                errorMessage =
                    error.details.validationMessages
                        ?.map((validationMessage) => validationMessage.message)
                        .join('\n') ?? '';
            }
            setError(errorMessage);
            return;
        }
        if (response) {
            // Doing this to not be dependent on Backend changes
            if (isResponseWrapper(response)) {
                const salaryDTO = response.value;
                const numTransactions = Math.ceil(salaryDTO.numberEmployees / maxEmployeesPerSalaryTransaction);
                setNumberTransactions(numTransactions);
                const numEmployees = salaryDTO.numberEmployees;
                setNumberEmployees(numEmployees);
                setFirstWageTransactionId(salaryDTO.id);
                return;
            }

            const salaryDTOs = response.values;
            setNumberTransactions(response.fullResultSize);
            setNumberEmployees(employmentIds.length);
            setFirstWageTransactionId(salaryDTOs[0].id);
        }
    };

    if (firstWageTransactionId === 0) {
        return (
            <CreateSalaryModalStandard
                awaitingResponse={awaitingResponse}
                handleClose={handleClose}
                period={period}
                submitForm={submitForm}
                isHistorical={isHistorical}
                error={error}
            />
        );
    } else if (severalTransactionsCreated) {
        return (
            <CreateSalaryModalInformSeveral
                handleClose={handleClose}
                closeDialogAndRedirect={closeDialogAndRedirect}
                maxNumberEmployees={maxEmployeesPerSalaryTransaction}
                numberEmployees={numberEmployees}
                numberTransactions={numberTransactions}
            />
        );
    } else {
        return <></>;
    }
}
